import React, { useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { categories, collections } from '../data/products'
import { useCart } from '../contexts/CartContext'
import { useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import MediaWithShimmer from '../components/MediaWithShimmer'

const Shop = () => {
  const { language } = useLanguage()
  const { cart, addToCart } = useCart()
  const { canViewHidden } = useAuth()
  const [searchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedSubcategory, setSelectedSubcategory] = useState('all')
  const [selectedCollection, setSelectedCollection] = useState('all')
  const [priceRange, setPriceRange] = useState([0, 2000000])
  const [sortBy, setSortBy] = useState('default')
  const [showFilters, setShowFilters] = useState(false)
  const [expandedCategory, setExpandedCategory] = useState(null)
  const [draftCategory, setDraftCategory] = useState('all')
  const [draftCollection, setDraftCollection] = useState('all')
  const [draftPriceRange, setDraftPriceRange] = useState([0, 2000000])
  const draftsChanged =
    draftCategory !== selectedCategory ||
    draftCollection !== selectedCollection ||
    draftPriceRange[0] !== priceRange[0] ||
    draftPriceRange[1] !== priceRange[1]
  const [dbProducts, setDbProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [hoveredProduct, setHoveredProduct] = useState(null)
  const [currentImageIndex, setCurrentImageIndex] = useState({})
  const [headerScrolled, setHeaderScrolled] = useState(false)

  useEffect(() => {
    const checkHeader = () => {
      const header = document.getElementById('header')
      if (header) {
        setHeaderScrolled(header.classList.contains('header--scrolled'))
      }
    }
    
    checkHeader()
    window.addEventListener('scroll', checkHeader)
    return () => window.removeEventListener('scroll', checkHeader)
  }, [])

  // Read URL params and set filters
  useEffect(() => {
    const categoryParam = searchParams.get('category')
    const collectionParam = searchParams.get('collection')
    
    // Если нет параметров в URL, сбрасываем все фильтры
    if (!categoryParam && !collectionParam) {
      setSelectedCategory('all')
      setSelectedSubcategory('all')
      setSelectedCollection('all')
      setSearchQuery('')
      setPriceRange([0, 2000000])
      setSortBy('default')
      return
    }
    
    if (categoryParam) {
      // Устанавливаем подкатегорию напрямую из параметра
      setSelectedSubcategory(categoryParam)
      
      // Если это Kids или Woman, устанавливаем категорию dresses
      if (categoryParam === 'Kids' || categoryParam === 'Woman') {
        setSelectedCategory('dresses')
      } else {
        // Для других категорий (Luxury, Cocktail и т.д.) сбрасываем основную категорию
        setSelectedCategory('all')
      }
    }
    
    if (collectionParam) {
      setSelectedCollection(collectionParam)
    }
  }, [searchParams])

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true)
      setError('')
      try {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .order('id', { ascending: true })
        if (error) {
          setError(error.message || (language === 'ru' ? 'Ошибка загрузки товаров' : language === 'es' ? 'Error al cargar productos' : 'Failed to load products'))
          setDbProducts([])
          return
        }
        const normalized = (data || []).map(p => ({
          ...p,
          nameEn: p.nameEn ?? p.nameen ?? '',
          nameEs: p.nameEs ?? p.namees ?? '',
          descriptionEn: p.descriptionEn ?? p.descriptionen ?? '',
          descriptionEs: p.descriptionEs ?? p.descriptiones ?? '',
          images: Array.isArray(p.images) && p.images.length > 0 ? p.images : (p.image ? [p.image] : [])
        }))
        setDbProducts(normalized)
      } catch (e) {
        setError(e?.message || (language === 'ru' ? 'Ошибка загрузки товаров' : language === 'es' ? 'Error al cargar productos' : 'Failed to load products'))
        setDbProducts([])
      } finally {
        setLoading(false)
      }
    }
    fetchProducts()
  }, [language])

  const filteredProducts = useMemo(() => {
    let filtered = dbProducts

    if (!canViewHidden) {
      filtered = filtered.filter(p => !p.is_hidden)
    }

    // Если выбрана подкатегория (Kids или Woman), фильтруем по ней
    if (selectedSubcategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedSubcategory)
    } 
    // Если выбрана категория dresses (но не подкатегория), показываем все платья
    else if (selectedCategory === 'dresses') {
      filtered = filtered.filter(p => p.category === 'Kids' || p.category === 'Woman')
    }
    // Если выбрано "Все", показываем все товары (уже filtered = dbProducts)

    if (selectedCollection !== 'all') {
      filtered = filtered.filter(p => p.collection === selectedCollection)
    }

    filtered = filtered.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1])

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.nameEn.toLowerCase().includes(query) ||
        p.nameEs.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query)
      )
    }

    switch (sortBy) {
      case 'price-asc':
        filtered = [...filtered].sort((a, b) => a.price - b.price)
        break
      case 'price-desc':
        filtered = [...filtered].sort((a, b) => b.price - a.price)
        break
      case 'name-asc':
        filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name))
        break
      default:
        break
    }

    return filtered
  }, [searchQuery, selectedCategory, selectedSubcategory, selectedCollection, priceRange, sortBy, dbProducts, canViewHidden])

  const formatPrice = (price) => {
    const formatted = new Intl.NumberFormat('ru-RU').format(price).replace(/\s/g, '\u00A0')
    return `${formatted}\u00A0€`
  }

  const getProductName = (product) => {
    return language === 'en' ? product.nameEn : language === 'es' ? product.nameEs : product.name
  }

  // Fix dash rendering in Bugaki font
  const renderProductName = (product) => {
    const name = getProductName(product)
    if (!name) return ''
    
    // Replace hyphens/dashes with a span that uses a different font
    const parts = name.split(/(-|–|—)/)
    return parts.map((part, index) => {
      if (part === '-' || part === '–' || part === '—') {
        return <span key={index} style={{ fontFamily: 'Georgia, serif' }}> - </span>
      }
      return part
    })
  }

  const translations = {
    title: { ru: 'Магазин', en: 'Shop', es: 'Tienda' },
    search: { ru: 'Поиск товаров...', en: 'Search products...', es: 'Buscar productos...' },
    filters: { ru: 'Фильтры', en: 'Filters', es: 'Filtros' },
    category: { ru: 'Категория', en: 'Category', es: 'Categoría' },
    collection: { ru: 'Коллекция', en: 'Collection', es: 'Colección' },
    priceRange: { ru: 'Диапазон цен', en: 'Price Range', es: 'Rango de Precio' },
    sortBy: { ru: 'Сортировать', en: 'Sort By', es: 'Ordenar' },
    sortDefault: { ru: 'По умолчанию', en: 'Default', es: 'Por Defecto' },
    sortPriceAsc: { ru: 'Цена: по возрастанию', en: 'Price: Low to High', es: 'Precio: Menor a Mayor' },
    sortPriceDesc: { ru: 'Цена: по убыванию', en: 'Price: High to Low', es: 'Precio: Mayor a Menor' },
    sortNameAsc: { ru: 'По названию', en: 'By Name', es: 'Por Nombre' },
    productsFound: { ru: 'Найдено товаров', en: 'Products Found', es: 'Productos Encontrados' },
    viewDetails: { ru: 'Подробнее', en: 'View Details', es: 'Ver Detalles' },
    hideFilters: { ru: 'Скрыть фильтры', en: 'Hide Filters', es: 'Ocultar Filtros' },
    showFilters: { ru: 'Фильтры', en: 'Filters', es: 'Filtros' },
    applyFilters: { ru: 'Применить фильтры', en: 'Apply Filters', es: 'Aplicar filtros' }
  }

  const t = translations

  return (
    <div className="min-h-screen bg-white pt-40 md:pt-40">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="mb-6">
          {/* Search Bar */}
          <div className="flex justify-end lg:justify-center mb-8">
            <div className="relative w-full max-w-md">
              <input
                type="text"
                placeholder={t.search[language]}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 border-b border-gray-300 focus:outline-none focus:border-black transition-colors montserrat bg-transparent text-gray-600"
              />
              <i className="ri-search-line absolute right-0 top-1/2 transform -translate-y-1/2 text-gray-600 text-2xl"></i>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-8 montserrat uppercase tracking-wide">
            {t.title[language]}
          </h1>

          {/* Categories - horizontal scroll on mobile */}
          <div className="overflow-x-auto pb-4 mb-6 -mx-4 px-4 scrollbar-gray">
            <div className="flex gap-2 min-w-max justify-center">
              <button
                onClick={() => {
                  setSelectedCategory('all')
                  setSelectedSubcategory('all')
                }}
                className={`px-6 py-3 rounded-full transition-colors montserrat text-sm whitespace-nowrap ${
                  selectedCategory === 'all'
                    ? 'bg-black text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-black'
                }`}
              >
                {categories[0].name[language]}
              </button>
              <button
                onClick={() => {
                  setSelectedCategory('dresses')
                  setSelectedSubcategory('all')
                }}
                className={`px-6 py-3 rounded-full transition-colors montserrat text-sm whitespace-nowrap ${
                  selectedCategory === 'dresses' && selectedSubcategory === 'all'
                    ? 'bg-black text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-black'
                }`}
              >
                {categories[1].name[language]}
              </button>
              {categories[1].subcategories?.map((subcat) => (
                <button
                  key={subcat.id}
                  onClick={() => {
                    setSelectedCategory('dresses')
                    setSelectedSubcategory(subcat.id)
                  }}
                  className={`px-6 py-3 rounded-full transition-colors montserrat text-sm whitespace-nowrap ${
                    selectedSubcategory === subcat.id
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-black'
                  }`}
                >
                  {subcat.name[language]}
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Filters drawer (animated, narrower width) */}
        <div className="flex flex-col">
          <div>
            {/* Filters drawer */}
            <div className="fixed inset-0 z-40 pointer-events-none">
              {/* Overlay */}
              <div
                className={`absolute left-0 right-0 bottom-0 top-0 bg-black/40 transition-opacity duration-300 ${showFilters ? 'opacity-100 pointer-events-auto' : 'opacity-0'}`}
                onClick={() => setShowFilters(false)}
              />
              {/* Panel */}
              <div
                className={`absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-xl overflow-y-auto transform transition-transform duration-300 ease-out ${showFilters ? 'translate-x-0 pointer-events-auto' : 'translate-x-full'}`}
              >
                {/* Search at top - responsive padding for header */}
                <div className={`bg-white border-b border-gray-200 pb-4 px-4 transition-all duration-300 ${headerScrolled ? 'pt-[6rem]' : 'pt-[10rem]'}`}>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={t.search[language]}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-4 py-3 pr-10 border-b border-gray-300 focus:outline-none focus:border-black transition-colors montserrat bg-transparent text-gray-600"
                    />
                    <i className="ri-search-line absolute right-0 top-1/2 transform -translate-y-1/2 text-gray-600 text-xl"></i>
                  </div>
                  <button 
                    onClick={() => setShowFilters(false)}
                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-black"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>

                {/* Categories List */}
                <div className="divide-y divide-gray-200">
                  {categories.map((cat) => (
                    <div key={cat.id}>
                      <button
                        onClick={() => {
                          if (cat.subcategories) {
                            setExpandedCategory(expandedCategory === cat.id ? null : cat.id)
                          } else {
                            setSelectedCategory(cat.id)
                            setSelectedSubcategory('all')
                            setShowFilters(false)
                          }
                        }}
                        className="w-full flex items-center justify-between px-6 py-5 hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-base font-bold montserrat uppercase tracking-wide">
                          {cat.name[language]}
                        </span>
                        {cat.subcategories ? (
                          <i className={`ri-arrow-${expandedCategory === cat.id ? 'down' : 'right'}-s-line text-xl text-gray-600 transition-transform`}></i>
                        ) : (
                          <i className="ri-arrow-right-s-line text-xl text-gray-600"></i>
                        )}
                      </button>
                      
                      {/* Subcategories */}
                      {cat.subcategories && expandedCategory === cat.id && (
                        <div className="bg-gray-50">
                          {cat.subcategories.map((subcat) => (
                            <button
                              key={subcat.id}
                              onClick={() => {
                                setSelectedCategory('dresses')
                                setSelectedSubcategory(subcat.id)
                                setShowFilters(false)
                              }}
                              className="w-full flex items-center justify-between px-10 py-4 hover:bg-gray-100 transition-colors border-b border-gray-200"
                            >
                              <span className="text-sm montserrat tracking-wide">
                                {subcat.name[language]}
                              </span>
                              <i className="ri-arrow-right-s-line text-lg text-gray-600"></i>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Collections List - скрыто, так как коллекции удалены */}
                {collections.length > 1 && (
                  <div className="border-t-4 border-gray-200 mt-4">
                    {collections.slice(1).map((col) => (
                      <button
                        key={col.id}
                        onClick={() => {
                          setSelectedCollection(col.id)
                          setShowFilters(false)
                        }}
                        className="w-full flex items-center justify-between px-6 py-5 hover:bg-gray-50 transition-colors border-b border-gray-200"
                      >
                        <span className="text-base font-bold montserrat uppercase tracking-wide">
                          {col.name[language]}
                        </span>
                        <i className="ri-arrow-right-s-line text-xl text-gray-600"></i>
                      </button>
                    ))}
                  </div>
                )}

                {/* Price Range */}
                <div className="border-t-4 border-gray-200 mt-4 p-6">
                  <h3 className="text-lg font-medium mb-4 playfair">{t.priceRange[language]}</h3>
                  <div className="space-y-4">
                    <input
                      type="range"
                      min="0"
                      max="2000000"
                      step="10000"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-600 montserrat">
                      <span>0 €</span>
                      <span>{formatPrice(priceRange[1])}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Products count and Filters button - moved above products */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600 montserrat text-lg">
              {filteredProducts.length} {language === 'ru' ? 'Товаров' : language === 'es' ? 'Productos' : 'Products'}
            </p>
            <button
              onClick={() => {
                setDraftCategory(selectedCategory)
                setDraftCollection(selectedCollection)
                setDraftPriceRange(priceRange)
                setShowFilters(true)
              }}
              className="flex items-center gap-2 text-gray-700 montserrat text-lg hover:text-black transition-colors"
            >
              <span>{t.filters[language]}</span>
              <i className="ri-equalizer-line text-xl"></i>
            </button>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Admin product creation form moved to Account page */}

            {error && (
              <div className="text-center py-10 text-red-600 montserrat">{error}</div>
            )}

            {loading ? (
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-6 sm:gap-3 lg:gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="w-full sm:w-[calc(50%-0.375rem)] lg:w-[calc(25%-0.75rem)] sm:flex-shrink-0">
                    <div className="relative overflow-hidden aspect-[3/4] bg-gray-200 animate-pulse mb-3" />
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-2 animate-pulse" />
                    <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-6 sm:gap-3 lg:gap-4">
                  {filteredProducts.map((product) => {
                    const productImages = Array.isArray(product.images) && product.images.length > 0 ? product.images : (product.image ? [product.image] : [])
                    const currentIndex = currentImageIndex[product.id] || 0
                    
                    return (
                    <div
                      key={product.id}
                      className="group bg-white w-full sm:w-[calc(50%-0.375rem)] lg:w-[calc(25%-0.75rem)] sm:flex-shrink-0"
                      onMouseEnter={() => setHoveredProduct(product.id)}
                      onMouseLeave={() => {
                        setHoveredProduct(null)
                        setCurrentImageIndex(prev => ({ ...prev, [product.id]: 0 }))
                      }}
                    >
                      {/* Product Name Above Image */}
                      <Link to={`/shop/${product.id}`} className="block mb-2">
                        <h3 className="text-3xl md:text-4xl bugaki capitalize leading-tight text-center font-black" style={{ color: '#FF1493' }}>
                          {renderProductName(product)}
                        </h3>
                      </Link>
                      
                      {/* Image Container */}
                      <Link
                        to={`/shop/${product.id}`}
                        className="block relative overflow-hidden aspect-[3/4] mb-3"
                        onMouseMove={(e) => {
                          if (productImages.length <= 1) return
                          const rect = e.currentTarget.getBoundingClientRect()
                          const x = e.clientX - rect.left
                          const segmentWidth = rect.width / productImages.length
                          const index = Math.min(Math.floor(x / segmentWidth), productImages.length - 1)
                          setCurrentImageIndex(prev => ({ ...prev, [product.id]: index }))
                        }}
                        onTouchStart={(e) => {
                          if (productImages.length <= 1) return
                          const touch = e.touches[0]
                          e.currentTarget.dataset.touchStartX = touch.clientX
                          e.currentTarget.dataset.touchStartY = touch.clientY
                          e.currentTarget.dataset.swiped = 'false'
                        }}
                        onTouchMove={(e) => {
                          if (productImages.length <= 1) return
                          const touch = e.touches[0]
                          const startX = parseFloat(e.currentTarget.dataset.touchStartX || '0')
                          const startY = parseFloat(e.currentTarget.dataset.touchStartY || '0')
                          const currentX = touch.clientX
                          const currentY = touch.clientY
                          const diffX = Math.abs(startX - currentX)
                          const diffY = Math.abs(startY - currentY)
                          const swiped = e.currentTarget.dataset.swiped === 'true'
                          
                          // Prevent page scroll if horizontal movement is detected
                          if (diffX > 10 && diffX > diffY) {
                            e.preventDefault()
                          }
                          
                          if (diffX > diffY && diffX > 80 && !swiped) {
                            e.currentTarget.dataset.swiped = 'true'
                            
                            if (startX - currentX > 0) {
                              const newIndex = Math.min(currentIndex + 1, productImages.length - 1)
                              if (newIndex !== currentIndex) {
                                setCurrentImageIndex(prev => ({ ...prev, [product.id]: newIndex }))
                              }
                            } else {
                              const newIndex = Math.max(currentIndex - 1, 0)
                              if (newIndex !== currentIndex) {
                                setCurrentImageIndex(prev => ({ ...prev, [product.id]: newIndex }))
                              }
                            }
                          }
                        }}
                        onTouchEnd={(e) => {
                          if (productImages.length <= 1) return
                          e.currentTarget.dataset.touchStartX = '0'
                          e.currentTarget.dataset.touchStartY = '0'
                          e.currentTarget.dataset.swiped = 'false'
                        }}
                      >
                        {/* Image container with all images */}
                        <div 
                          className="flex w-full h-full transition-transform duration-300 ease-out"
                          style={{ 
                            transform: `translateX(-${currentIndex * 100}%)`
                          }}
                        >
                          {productImages.map((img, idx) => (
                            <div key={idx} className="w-full h-full flex-shrink-0">
                              <MediaWithShimmer
                                src={img}
                                alt={`${getProductName(product)} - ${idx + 1}`}
                                className="w-full h-full object-contain"
                                eager={idx === 0}
                              />
                            </div>
                          ))}
                        </div>
                        
                        {/* Badge top-left */}
                        {(product.is_set || product.badge) && (
                          <div className="absolute top-3 left-3 pointer-events-none">
                            {product.badge && !product.is_set ? (
                              <span className="bg-white text-gray-800 px-3 py-1 text-[10px] uppercase tracking-wider montserrat font-normal">
                                {product.badge}
                              </span>
                            ) : product.is_set ? (
                              <span className="bg-white text-gray-800 px-3 py-1 text-[10px] uppercase tracking-wider montserrat font-normal">
                                {language === 'ru' ? 'Комплект' : language === 'es' ? 'Conjunto' : 'Set'}
                              </span>
                            ) : null}
                          </div>
                        )}
                        
                        {/* Wishlist icon top-right */}
                        <button 
                          className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center hover:opacity-70 transition-opacity z-10"
                          onClick={(e) => {
                            e.preventDefault()
                            // Add wishlist functionality here
                          }}
                        >
                          <i className="ri-heart-line text-2xl text-gray-700"></i>
                        </button>
                        
                        {/* Dots indicator */}
                        {productImages.length > 1 && (
                          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2 pointer-events-none">
                            {productImages.map((_, idx) => (
                              <div
                                key={idx}
                                className={`rounded-full transition-all duration-300 ${
                                  idx === currentIndex 
                                    ? 'bg-black w-2 h-2' 
                                    : 'bg-gray-400 w-1.5 h-1.5'
                                }`}
                              />
                            ))}
                          </div>
                        )}
                      </Link>
                      
                      {/* Price and Cart */}
                      <div className="flex items-start justify-between gap-2">
                        <Link to={`/shop/${product.id}`} className="flex-1">
                          <p className="text-sm sm:text-base font-normal montserrat text-gray-900">
                            {formatPrice(product.price)}
                          </p>
                        </Link>
                        <button 
                          className="flex-shrink-0 w-8 h-8 flex items-center justify-center hover:opacity-70 transition-opacity relative"
                          onClick={(e) => {
                            e.preventDefault()
                            addToCart(product, '', '')
                          }}
                        >
                          <i className="ri-shopping-bag-line text-xl text-gray-700"></i>
                          {(() => {
                            const cartQty = cart.filter(item => item.id === product.id).reduce((sum, item) => sum + item.quantity, 0)
                            return cartQty > 0 ? (
                              <span className="absolute -top-1 -right-1 bg-black text-white text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
                                {cartQty}
                              </span>
                            ) : null
                          })()}
                        </button>
                      </div>
                    </div>
                  )})}
                </div>

                {!loading && filteredProducts.length === 0 && (
                  <div className="text-center py-20">
                    <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500 montserrat">
                      {language === 'ru' ? 'Товары не найдены' : language === 'en' ? 'No products found' : 'No se encontraron productos'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Shop

